
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useCompanyStore } from '@/lib/stores';
import { useToast } from '@/hooks/use-toast';

export const useCompanyImages = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [bannerFile, setBannerFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { updateCompany, fetchCompanies } = useCompanyStore();
  
  // Fetch company data including logo and banner on initial load
  useEffect(() => {
    const loadCompanyData = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('companies')
          .select('logo, banner_image')
          .eq('id', user.user_metadata.company_id)
          .single();
          
        if (error) {
          console.error("Error fetching company data:", error);
          return;
        }
        
        if (data) {
          if (data.logo) setLogoPreview(data.logo);
          if (data.banner_image) setBannerPreview(data.banner_image);
        }
      } catch (e) {
        console.error("Error in loadCompanyData:", e);
      }
    };
    
    loadCompanyData();
  }, [user]);
  
  // Handle logo file change
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview the logo
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setLogoPreview(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
    
    // Store the file for later upload
    setLogoFile(file);
  };
  
  // Handle banner file change
  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Preview the banner
    const reader = new FileReader();
    reader.onload = (e) => {
      if (e.target?.result) {
        setBannerPreview(e.target.result as string);
      }
    };
    reader.readAsDataURL(file);
    
    // Store the file for later upload
    setBannerFile(file);
  };
  
  // Handle removing the logo
  const handleRemoveLogo = () => {
    setLogoPreview(null);
    setLogoFile(null);
  };
  
  // Handle removing the banner
  const handleRemoveBanner = () => {
    setBannerPreview(null);
    setBannerFile(null);
  };
  
  // Upload file to Supabase storage
  const uploadFile = async (file: File, prefix: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${user?.id}-${Date.now()}.${fileExt}`;
      const filePath = `${prefix}/${fileName}`;
      
      // Upload to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('company-assets')
        .upload(filePath, file);
        
      if (uploadError) {
        console.error(`Error uploading ${prefix}:`, uploadError);
        toast({
          title: "Upload Failed",
          description: `There was a problem uploading your ${prefix.replace('-', ' ')}.`,
          variant: "destructive"
        });
        return null;
      }
      
      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from('company-assets')
        .getPublicUrl(filePath);
        
      return publicUrlData.publicUrl;
    } catch (error) {
      console.error(`Error in uploading ${prefix}:`, error);
      return null;
    }
  };
  
  // Submit changes to the database
  const handleSubmit = async () => {
    if (!user?.id) return false;
    
    setIsLoading(true);
    
    try {
      const updates: { logo?: string | null, banner_image?: string | null } = {};
      
      // If there's a new logo file, upload it
      if (logoFile) {
        const logoUrl = await uploadFile(logoFile, 'company-logos');
        if (logoUrl) {
          updates.logo = logoUrl;
        }
      } else if (logoPreview === null) {
        // If logo was removed, set it to null
        updates.logo = null;
      }
      
      // If there's a new banner file, upload it
      if (bannerFile) {
        const bannerUrl = await uploadFile(bannerFile, 'company-banners');
        if (bannerUrl) {
          updates.banner_image = bannerUrl;
        }
      } else if (bannerPreview === null) {
        // If banner was removed, set it to null
        updates.banner_image = null;
      }
      
      // Only update if we have changes
      if (Object.keys(updates).length > 0) {
        // Update company record
        const success = await updateCompany(user.user_metadata.company_id, updates);
        
        if (success) {
          // Refresh companies data
          await fetchCompanies();
          toast({
            title: "Success",
            description: "Company images have been updated successfully.",
          });
          setLogoFile(null);
          setBannerFile(null);
        } else {
          toast({
            title: "Update Failed",
            description: "There was a problem updating your company images.",
            variant: "destructive"
          });
        }
      } else {
        toast({
          title: "No Changes",
          description: "No changes were made to your company images.",
        });
      }
      
      setIsLoading(false);
      return true;
    } catch (e) {
      console.error("Error in handleSubmit:", e);
      toast({
        title: "Error",
        description: "An unexpected error occurred. Please try again.",
        variant: "destructive"
      });
      setIsLoading(false);
      return false;
    }
  };
  
  return {
    logoPreview,
    bannerPreview,
    isLoading,
    handleLogoChange,
    handleBannerChange,
    handleRemoveLogo,
    handleRemoveBanner,
    handleSubmit
  };
};

